import { useUpdateOrderQuoteOfferBatchMutation } from '@codezo/graphql';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { DatePicker } from '../../ui/date-picker';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

export function EditBatchModal({
	open,
	onOpenChange,
	batchId,
	initialValues,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	batchId: string;
	initialValues: {
		name: string;
		acceptDeadline?: string | null;
	};
	onSuccess: () => void;
}) {
	const [name, setName] = useState('');
	const [acceptDeadline, setAcceptDeadline] = useState<Date | null>(null);
	const [updateBatch, { loading }] = useUpdateOrderQuoteOfferBatchMutation();

	useEffect(() => {
		if (open) {
			setName(initialValues.name);
			setAcceptDeadline(
				initialValues.acceptDeadline ? new Date(initialValues.acceptDeadline) : null
			);
		}
	}, [open, initialValues]);

	const handleSubmit = async () => {
		if (!name.trim()) return;
		try {
			await updateBatch({
				variables: {
					id: batchId,
					input: {
						name: name.trim(),
						acceptDeadline: acceptDeadline?.toISOString() || null,
					},
				},
			});
			toast.success('Offer updated');
			onOpenChange(false);
			onSuccess();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update');
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Edit offer</DialogTitle>
				</DialogHeader>
				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-1.5">
								<Label>
									Name <span className="text-destructive">*</span>
								</Label>
								<Input value={name} onChange={(e) => setName(e.target.value)} />
							</div>
							<div className="flex flex-col gap-1.5">
								<Label>Accept deadline</Label>
								<DatePicker
									value={acceptDeadline}
									onChange={(d) => setAcceptDeadline(d ?? null)}
									placeholder="Select date"
									minDate={new Date()}
								/>
								<div className="flex gap-1.5 pt-1">
									{[
										{ label: '3 days', days: 3 },
										{ label: '1 week', days: 7 },
										{ label: '2 weeks', days: 14 },
									].map((opt) => (
										<button
											key={opt.days}
											type="button"
											onClick={() => {
												const d = new Date();
												d.setDate(d.getDate() + opt.days);
												setAcceptDeadline(d);
											}}
											className="rounded-md border border-line-2 bg-surface-input px-2 py-0.5 text-xs text-text-3 transition-colors hover:border-line-3 hover:text-text-1"
										>
											{opt.label}
										</button>
									))}
								</div>
							</div>
						</div>
					</DialogBody>
					<DialogFooter>
						<Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
							Cancel
						</Button>
						<Button type="submit" disabled={!name.trim() || loading}>
							{loading ? 'Saving...' : 'Save'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
